import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'
import Download from '@mui/icons-material/Download'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { BUTTON_LOADING_ICON } from '../../component/BottomButtonGroup'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, LIST_FILTER_WIDE_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import * as FileSaver from "file-saver"
import * as XLS from "xlsx"
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import { formatDate, renderDateTime } from '../../utils/dateDisplay'
import { getRecordMailStatus } from './recordMailStatus'

const DEFAULT_SEARCH_SELECT = {
    searchType: 0,
    searchStatus: 99,
    productId: 0,
}

const SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    productId: {
        param: 'product',
        defaultValue: DEFAULT_SEARCH_SELECT.productId,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const RECORD_TABLE_COLUMNS = [
    { width: 70 },
    { width: 120 },
    { width: 280 },
    { width: 140 },
    { width: 260 },
    { width: 100 },
    { width: 140 },
    { width: 180 },
    { width: 180 },
    { width: 120 },
]

const RECORD_TABLE_MIN_WIDTH = RECORD_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const RECORD_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const RECORD_DATE_CELL_SX = {
    whiteSpace: 'nowrap',
}

const getRecordCourseLabel = (row) => {
    if (row.type) {
        return row.course || '-'
    }

    const title = String(row.title || '').replaceAll('2023 랭킹마라톤', '').trim()
    const course = row.course || ''

    return [title, course].filter(Boolean).join(' ') || '-'
}

export default function Records({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const deleteInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [sendingAws, setSendingAws] = useState(false)
    const [batchResult, setBatchResult] = useState(null)
    const [batchResultLabel, setBatchResultLabel] = useState('')
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const searchTextRef = useRef(initialListState.searchText)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const searchSelectRef = useRef(initialListState.searchSelect)
    const [products, setProducts] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const silentListRefreshRef = useRef(false)

    const getProductCategory = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/record/getProductCategory', { searchText: searchTextRef.current, searchSelect: searchSelectRef.current })
            if (data.code === 200) {
                setProducts(data.data)
            }
        } catch (_error) {
            setProducts([])
        }
    }, [])

    useEffect(() => {
        getProductCategory()
    }, [getProductCategory])

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/record/getList', {
                page: curPage,
                offset: offset,
                adminId: adminId,
                role: role,
                searchText: searchQuery,
                searchSelect: searchSelect
            })
            if (data.code === 200) {
                setTotalCount(data.data?.count || 0)
                setParams(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '완주기록증 신청 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '완주기록증 신청 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isExcelDownloading = actionLoading?.type === 'excel'
    const excelDownloadButtonLabel = isExcelDownloading ? '엑셀 다운로드 중...' : '엑셀 다운로드'
    const awsSendButtonLabel = sendingAws ? 'SES 접수 중...' : '전체 메일 발송'
    const isBusy = Boolean(actionLoading) || sendingAws

    const getBatchReasonLabel = (reason) => {
        if (reason === 'missing_template') return '템플릿 이미지 없음'
        if (reason === 'invalid_number') return '배번 0000 제외'
        if (reason === 'missing_email') return '이메일 없음'
        if (reason === 'invalid_email') return '이메일 형식 오류'
        if (reason === 'suppressed_bounce') return 'SES 반송 억제목록'
        if (reason === 'suppressed_complaint') return 'SES 신고 억제목록'
        if (reason === 'send_failed') return '메일 발송 실패'
        return reason || '-'
    }


    const updateSearchSelect = (e) => {
        if (isBusy) {
            return
        }

        const { name, value } = e.target
        const trimmedSearchText = (searchText || '').trim()
        const nextSearchSelect = {
            ...searchSelect,
            [name]: value
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        searchSelectRef.current = nextSearchSelect
        setSearchQuery(trimmedSearchText)
        setCurPage(1)
        setSearchSelect(nextSearchSelect)
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        const nextSearchSelect = {
            ...DEFAULT_SEARCH_SELECT,
        }

        searchTextRef.current = ''
        searchSelectRef.current = nextSearchSelect
        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect(nextSearchSelect)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current.has(id) || sendingAws) {
            return
        }

        try {
            deleteInFlightRef.current.add(id)
            setActionLoading({ type: 'delete', id })

            const { data } = await API.post('/api/v1/admin/record/delete', { recordId: id })
            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)

                if (curPage > 1 && params.length === 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData(false)
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('완주기록증 신청을 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current.delete(id)
            setActionLoading(null)
        }
    }

    const handleAllSend = async () => {
        if (isBusy) return

        setSendingAws(true)
        setBatchResult(null)
        setBatchResultLabel('AWS')

        try {
            const { data } = await API.post('/api/v1/admin/record/allSend', {
                searchText: searchQuery,
                searchSelect,
            })
            if (data.code === 200) {
                setBatchResult(data.data || null)
                const hasIssue = Number(data.data?.failed || 0) > 0 ||
                    Number(data.data?.skippedMissingTemplate || 0) > 0 ||
                    Number(data.data?.skippedInvalidNumber || 0) > 0 ||
                    Number(data.data?.skippedMissingEmail || 0) > 0 ||
                    Number(data.data?.skippedInvalidEmail || 0) > 0 ||
                    Number(data.data?.skippedSuppressed || 0) > 0

                setOpenSnackbar(true)
                setSeveritySnackbar(hasIssue ? 'warning' : 'success')
                setMessageSnackbar(data.message)
                getData(false)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('완주기록증 AWS 전체발송에 실패했습니다.')
        } finally {
            setSendingAws(false)
        }
    }

    const exportToCSV = async (fileName) => {
        if (isBusy) {
            return
        }

        setActionLoading({ type: 'excel' })

        try {
            const { data } = await API.post('/api/v1/admin/record/getListToExcel', { searchText: searchQuery, searchSelect: searchSelect, })
            if (data.code === 200) {

                let jsonArr = []

                data.data.forEach(row => {
                    let tempObj = {
                        num: row.rownum, userId: row.userId, 코스: row.title + ' ' + row.course, 이름: row.username, 이메일: row.email, 배번호: row.number,
                        신청일: row.regTime, 신청기기: row.category
                    }
                    jsonArr.push(tempObj)
                })

                const ws = XLS.utils.json_to_sheet(jsonArr)
                const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
                const excelBuffer = XLS.write(wb, { bookType: 'xlsx', type: 'array' })
                const excelData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' })
                const now = new Date()
                FileSaver.saveAs(excelData, `${fileName}-${formatDate(now)}.xls` )
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('완주기록증 신청 엑셀 데이터를 불러오지 못했습니다.')
        } finally {
            setActionLoading(null)
        }
    }

    // 검색어와 선택 필터가 기본값에서 벗어났는지 확인해 빈 상태 안내를 구분한다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? defaultValue

            return String(currentValue) !== String(defaultValue)
        })
    }

    // 목록 행이 없을 때 오류, 필터 결과 없음, 기본 빈 목록을 명확히 보여준다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="완주기록증 신청 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={isBusy}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 완주기록증 신청이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="접수된 완주기록증 신청이 없습니다."
                description="신청 내역이 접수되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>완주기록증 신청 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="userId/이름/이메일/배번"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => {
                                    searchTextRef.current = e.target.value
                                    setSearchText(e.target.value)
                                }}
                                disabled={isBusy}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            {/* <TextField
                                label="종류"
                                select
                                name="searchType"
                                value={searchSelect.searchType}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={99}>타입</MenuItem>
                                <MenuItem value={0}>완주기록증</MenuItem>
                                <MenuItem value={1}>완등기록증</MenuItem>
                            </TextField> */}
                            <TextField
                                required
                                label="코스"
                                select
                                id="productId"
                                name="productId"
                                value={searchSelect.productId}
                                size="small"
                                sx={LIST_FILTER_WIDE_SELECT_FIELD_SX}
                                onChange={updateSearchSelect}
                                disabled={isBusy}
                            >
                                <MenuItem key={0} value={0} >{'전체'}</MenuItem>
                                {products.map((row, index) => (
                                    <MenuItem key={row.productId} value={row.productId} >{row.title}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="SES 접수상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                                disabled={isBusy}
                            >
                                <MenuItem value={99}>전체</MenuItem>
                                <MenuItem value={1}>접수 이력 있음</MenuItem>
                                <MenuItem value={0}>미접수</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    <ListTableToolbar
                        left={(
                            <Button
                                color="success"
                                variant="contained"
                                onClick={() => exportToCSV('랭킹마라톤-완주기록증')}
                                size="small"
                                disabled={isBusy}
                                startIcon={isExcelDownloading ? BUTTON_LOADING_ICON : <Download />}
                                aria-busy={isExcelDownloading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                            >
                                {excelDownloadButtonLabel}
                            </Button>
                        )}
                        right={(
                            <>
                                {role === 5 && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={sendingAws ? BUTTON_LOADING_ICON : <SendIcon />}
                                        aria-busy={sendingAws}
                                        size="small"
                                        onClick={handleAllSend}
                                        disabled={isBusy || Number(searchSelect.searchStatus) === 1}
                                        sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                    >
                                        {awsSendButtonLabel}
                                    </Button>
                                )}
                            </>
                        )}
                    />
                    {batchResult && (
                        <Paper variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: '#fff8e1' }}>
                            <Box sx={{ fontWeight: 700 }}>
                                최근 {batchResultLabel} 발송 결과
                            </Box>
                            <Box sx={{ mt: 1, fontSize: 14 }}>
                                총 {batchResult.total || 0}건 / 시도 {batchResult.attempted || 0}건 / SES 접수 {batchResult.sent || 0}건 / 실패 {batchResult.failed || 0}건
                            </Box>
                            <Box sx={{ fontSize: 14 }}>
                                템플릿 누락 {batchResult.skippedMissingTemplate || 0}건 / 배번 제외 {batchResult.skippedInvalidNumber || 0}건 / 이메일 누락 {batchResult.skippedMissingEmail || 0}건
                            </Box>
                            <Box sx={{ fontSize: 14 }}>
                                이메일 형식 오류 {batchResult.skippedInvalidEmail || 0}건 / SES 억제목록 제외 {batchResult.skippedSuppressed || 0}건
                            </Box>
                            {Array.isArray(batchResult.failItems) && batchResult.failItems.length > 0 && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Box sx={{ fontWeight: 700, color: '#c62828' }}>실패 항목</Box>
                                    {batchResult.failItems.map((item, index) => (
                                        <Box key={`fail-${item.recordId || 'x'}-${index}`} sx={{ fontSize: 13, mt: 0.5 }}>
                                            #{item.recordId} / {item.email || '-'} / {getBatchReasonLabel(item.reason)}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {Array.isArray(batchResult.skipItems) && batchResult.skipItems.length > 0 && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Box sx={{ fontWeight: 700, color: '#ef6c00' }}>스킵 항목</Box>
                                    {batchResult.skipItems.map((item, index) => (
                                        <Box key={`skip-${item.recordId || 'x'}-${index}`} sx={{ fontSize: 13, mt: 0.5 }}>
                                            #{item.recordId} / {item.email || '-'} / {getBatchReasonLabel(item.reason)}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    )}
                    {/* 테이블 */}
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: RECORD_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {RECORD_TABLE_COLUMNS.map((column, index) => (
                                <col key={`record-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>userId</TableCell>
                                <TableCell>코스</TableCell>
                                <TableCell>이름</TableCell>
                                <TableCell>이메일</TableCell>
                                <TableCell>배번</TableCell>
                                <TableCell>배송 상태</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell>수정일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? params.map((row) => (
                                <TableRow key={row.recordId}>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>{row.userId || '-'}</TableCell>
                                    <TableCell sx={RECORD_TEXT_CELL_SX}>{getRecordCourseLabel(row)}</TableCell>
                                    <TableCell sx={RECORD_TEXT_CELL_SX}>{row.username || '-'}</TableCell>
                                    <TableCell sx={RECORD_TEXT_CELL_SX}>{row.email || '-'}</TableCell>
                                    <TableCell>{row.number || '-'}</TableCell>
                                    <TableCell>{getRecordMailStatus({ status: row.status, deliveryStatus: row.deliveryStatus }).label}</TableCell>
                                    <TableCell sx={RECORD_DATE_CELL_SX}>{renderDateTime(row.regTime)}</TableCell>
                                    <TableCell sx={RECORD_DATE_CELL_SX}>{renderDateTime(row.updTime)}</TableCell>
                                    <TableCell align="right">
                                        <ListActionGroup disabled={isBusy}>
                                            <EditIcon path={'new-record'} id={row.recordId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} />
                                            <DeleteAlertDialog handleDelete={handleDelete} id={row.recordId}/>
                                        </ListActionGroup>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
